import { createFileRoute, Link } from "@tanstack/react-router";
import portrait from "@/assets/arda-event-portrait.webp.asset.json";
import landscape from "@/assets/arda-event-landscape.webp.asset.json";
import { PageIntro, PageSection } from "@/components/site/page";
import { AUTHOR_BOOKS, BOARDS, CAREER, CURRENT_WORK, EDUCATION, EMAIL, LINKEDIN_URL, SITE, personSchema, type Role } from "@/content/author";

const URL = `${SITE}/hakkimda`;
const TITLE = "Biyografi ve Çalışmalar — Arda Öztaşkın";
const DESC = "Arda Öztaşkın'ın biyografisi: bugünkü çalışmaları, mesleki deneyimi, eğitimi, kitapları ve akademik katkıları.";

export const Route = createFileRoute("/hakkimda")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:type", content: "profile" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({ "@context": "https://schema.org", "@type": "ProfilePage", inLanguage: "tr-TR", url: URL, mainEntity: personSchema() }),
      },
    ],
  }),
  component: AboutPage,
});

function Rows({ items }: { items: Role[] }) {
  return (
    <ul className="max-w-3xl">
      {items.map((r) => (
        <li key={`${r.role}-${r.org}`} className="grid gap-0.5 border-t border-ink/10 py-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:gap-6">
          <span>
            <span className="block font-serif text-[17px] leading-snug sm:text-[18px]">{r.role}</span>
            <span className="block text-[13px] text-ink/70">{r.org}</span>
          </span>
          {r.when && <span className="text-[13px] text-ink/65 sm:pt-1">{r.when}</span>}
        </li>
      ))}
    </ul>
  );
}

function AboutPage() {
  return (
    <>
      <PageIntro
        crumbs={[{ label: "Yazar", to: "/yazar" }, { label: "Biyografi ve Çalışmalar" }]}
        kicker="Biyografi"
        dot="bg-theme-tech"
        title="Biyografi ve Çalışmalar"
        lede={
          <div className="space-y-4">
            <p>Ankara'da doğdum; Ankara Üniversitesi Siyasal Bilgiler Fakültesi'ni bitirdim. Meslek hayatımı bankacılıkta iletişim alanında geçirdim ve 2008'den bu yana Yapı Kredi'deyim.</p>
            <p className="text-[17px] text-ink/70 sm:text-[19px]">İklim, yapay zekâ, ekonomi ve insan üzerine dört kitap yazdım; haftalık bir gazete köşem var ve üniversitede sürdürülebilirlik iletişimi dersi veriyorum.</p>
          </div>
        }
        aside={
          <picture>
            <source media="(min-width: 1024px)" srcSet={portrait.url} />
            <source media="(min-width: 640px)" srcSet={landscape.url} />
            <img
              src={portrait.url}
              alt="Arda Öztaşkın"
              loading="eager"
              width={768}
              height={960}
              className="aspect-[4/5] w-full object-cover object-center sm:aspect-[4/3] sm:max-h-[32rem] sm:object-[center_20%] lg:ml-auto lg:aspect-[4/5] lg:max-h-none lg:max-w-[24rem] lg:object-center"
            />
          </picture>
        }
      />

      <PageSection title="Bugünkü çalışmalar">
        <Rows items={CURRENT_WORK} />
      </PageSection>

      <PageSection title="Mesleki deneyim">
        <p className="mb-4 max-w-2xl text-[15px] leading-relaxed text-ink/80">
          Kariyerime Demirbank'ın reklam ve halkla ilişkiler bölümünde başladım; Dışbank ve Fortis Bank'ta iletişim görevlerinde, kurumsal geçiş süreçlerinde çalıştım. 2008'de Marka Yönetimi Direktörü olarak Yapı Kredi'ye katıldım; 2018'den bu yana kurumsal iletişimi yönetiyorum. Bu görev marka stratejisi, medya ve paydaş ilişkileri, itibar yönetimi ve sürdürülebilirlik iletişimini kapsıyor.
        </p>
        <Rows items={CAREER} />
      </PageSection>

      <PageSection title="Eğitim">
        <Rows items={EDUCATION} />
      </PageSection>

      <PageSection title="Yazarlık ve akademik katkılar">
        <div className="grid max-w-5xl gap-10 lg:grid-cols-2">
          <div>
            <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-clay">Kitaplar</h3>
            <ul>
              {AUTHOR_BOOKS.map((b) => (
                <li key={b.id} className="border-t border-ink/10 py-3">
                  <Link to={b.to} className="group block">
                    <span className="block font-serif text-[17px] group-hover:text-clay">{b.title}</span>
                    <span className="block text-[13px] text-ink/70">{b.year} · {b.publisher}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-clay">Ders, dernek ve kurullar</h3>
            <Rows items={[CURRENT_WORK[1], ...BOARDS]} />
            <p className="mt-3 text-[13px] leading-relaxed text-ink/65">Üniversitedeki katkım ders vermekle sınırlı; akademik unvanım veya akademik yayınım yok.</p>
          </div>
        </div>
        <p className="mt-6 text-[15px]">
          <Link to="/konusmalar" className="text-theme-tech underline">Konuşma kayıtları</Link>
        </p>
      </PageSection>

      <PageSection title="İletişim">
        <p className="text-[15px] text-ink/80">
          <a href={`mailto:${EMAIL.user}@${EMAIL.domain}`} className="text-theme-tech underline">{EMAIL.user}@{EMAIL.domain}</a>
          {" · "}
          <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer me" className="text-theme-tech underline">LinkedIn</a>
        </p>
      </PageSection>
    </>
  );
}
